import { Alert, WaterQuality, SafetyStatus, Cctv, Wearable } from '../types/domain';
import { WS_ORIGIN } from '../config';
import { getBearerTokenForApi } from '../utils/poolSiteAuth';

type WebSocketCallback<T> = (data: T) => void;
type ConnectionStatusCallback = (status: 'disconnected' | 'connecting' | 'connected') => void;

export class WebSocketService {
  public endpoint: string;
  private ws: WebSocket | null = null;
  private subscribers: Map<string, Set<WebSocketCallback<any>>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimeout = 3000;
  private isWebSocketEnabled: boolean = true;
  private currentUrl: string | null = null; // 현재 연결된 URL 추적
  private connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private pingInterval: NodeJS.Timeout | null = null;
  private pingTimeout: NodeJS.Timeout | null = null;
  private PING_INTERVAL_MS = 30000; // 30초마다 핑 전송
  private PONG_TIMEOUT_MS = 10000; // 10초 내 응답 없으면 연결 해제
  
  // 최근 수신 데이터 캐싱 (연결 복구 시 사용)
  private lastReceivedData: Map<string, any> = new Map();
  
  // 연결 상태 변경 구독자
  private connectionStatusSubscribers: Set<ConnectionStatusCallback> = new Set();

  private lastReconnectAttempt: Date | null = null;
  private connectedSince: Date | null = null;

  constructor(endpoint: string = 'monitoring') {
    this.endpoint = endpoint;
    // 환경변수로 WebSocket 기능을 비활성화할 수 있음
    this.isWebSocketEnabled = process.env.REACT_APP_ENABLE_WEBSOCKET !== 'false';
    if (!this.isWebSocketEnabled) {
      console.log('WebSocket 기능이 비활성화되어 있습니다.');
    }
  }

  // 연결 시작 (특정 poolId 사용)
  public connectToPool(poolId: string | number) {
    if (!this.isWebSocketEnabled) return;
    if (!poolId) {
        console.error("Pool ID is required to connect to pool WebSocket.");
        return;
    }
    const token = getBearerTokenForApi();
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    const url = `${WS_ORIGIN}/ws/pool/${poolId}${qs}`;
    this.connect(url);
  }

  /** 통합 관제 — 실시간 이벤트(/control-center) */
  public connectToControlCenter(token?: string | null) {
    if (!this.isWebSocketEnabled) return;
    const t = token ?? getBearerTokenForApi();
    if (!t) {
      console.error('Token is required for control-center WebSocket.');
      return;
    }
    const url = `${WS_ORIGIN}/control-center?token=${encodeURIComponent(t)}`;
    this.connect(url);
  }

  // 연결 시작 (범용) - 내부 및 외부에서 호출 가능
  public connect(url: string) {
    if (!this.isWebSocketEnabled) {
      console.log('WebSocket is disabled.');
      return;
    }
    if (this.connectionStatus === 'connected' && this.currentUrl === url) {
      console.log(`Already connected to ${url}.`);
      return;
    }
    if (this.connectionStatus === 'connecting') {
        console.log("WebSocket connection attempt already in progress.");
        return;
    }

    // 기존 연결이 있으면 먼저 해제
    this.disconnect(true); // 내부 호출 플래그 추가

    this.currentUrl = url;
    this.setConnectionStatus('connecting');
    console.log(`Connecting to WebSocket URL: ${this.currentUrl}`);

    this.lastReconnectAttempt = new Date();

    try {
      this.ws = new WebSocket(this.currentUrl);

      this.ws.onopen = () => {
        this.setConnectionStatus('connected');
        console.log(`WebSocket connected to ${this.currentUrl}`);
        this.reconnectAttempts = 0;
        this.connectedSince = new Date();
        this.subscribeToAll(); // 연결 성공 시 구독 재시도
        this.startHeartbeat(); // 하트비트 시작
        this.restoreLastData(); // 마지막 데이터 복구
      };

      this.ws.onmessage = (event) => {
        this.resetPingTimeout(); // 메시지 수신 시 핑 타임아웃 리셋

        // Pong 메시지 처리
        if (event.data === 'pong') {
          console.log('WebSocket: Received pong');
          return;
        }
        // Ping 메시지 처리 (서버가 보낼 경우)
        if (event.data === 'ping') {
          console.log('WebSocket: Received ping, sending pong');
          this.ws?.send('pong');
          return;
        }

        try {
          const message = JSON.parse(event.data as string);
          console.log('WebSocket message received:', message);
          // 메시지 타입에 따라 구독자에게 알림
          if (message.type && message.data) {
            this.lastReceivedData.set(message.type, message.data);
            this.notifySubscribers(message.type, message.data);
          } else if (
            message.type === 'risk_alert' ||
            message.type === 'risk_event' ||
            message.type === 'event' ||
            message.type === 'event_resolved'
          ) {
            this.lastReceivedData.set('events', message);
            this.notifySubscribers('events', message);
          } else if (message.type) {
            this.lastReceivedData.set(message.type, message);
            this.notifySubscribers(message.type, message);
          } else {
            console.warn('Received message without type or data:', message);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', event.data, error);
        }
      };

      this.ws.onclose = (event) => {
        console.log(`WebSocket disconnected from ${this.currentUrl}. Code: ${event.code}, Reason: ${event.reason}`);
        this.setConnectionStatus('disconnected');
        this.stopHeartbeat();
        // 재연결 시도 (사용자 의도 disconnect가 아닐 경우)
        if (!event.wasClean && this.isWebSocketEnabled) { // 'wasClean'으로 의도된 종료인지 확인
             this.handleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error(`WebSocket error on ${this.currentUrl}:`, error);
        this.setConnectionStatus('disconnected'); // 에러 시 연결 끊김으로 간주
        this.stopHeartbeat();
        // 에러 발생 시 바로 재연결 시도하지 않고 onclose에서 처리하도록 할 수 있음
        // 필요하다면 여기서 handleReconnect 호출 가능
      };
    } catch (error) {
      console.error(`WebSocket connection error for ${this.currentUrl}:`, error);
      this.setConnectionStatus('disconnected');
      this.stopHeartbeat();
      if (this.isWebSocketEnabled) {
           this.handleReconnect(); // 연결 자체 실패 시 재연결 시도
      }
    }
  }

  // 연결 해제
  public disconnect(internalCall = false) {
      console.log(`Disconnect requested. Internal: ${internalCall}`);
      this.stopHeartbeat();
      if (this.ws) {
          // 재연결 로직 비활성화 후 연결 종료
          if (!internalCall) { // 외부에서 disconnect 호출 시 재연결 방지
              this.isWebSocketEnabled = false; // 재연결 시도를 막기 위해 임시 비활성화 (다시 연결하려면 connect 명시적 호출 필요)
              console.log("WebSocket auto-reconnect disabled by explicit disconnect.");
          }
          this.ws.close(1000, "Client disconnected"); // 1000: Normal Closure
          this.ws = null;
      }
      this.setConnectionStatus('disconnected');
      this.currentUrl = null;
      console.log("WebSocket connection closed.");
       // disconnect 호출 시 재연결 시도 방지
      if (this.reconnectTimeout) {
           clearTimeout(this.reconnectTimeout);
           this.reconnectTimeout = 0; // Timeout ID 초기화
       }
      this.connectedSince = null;
  }

  // 하트비트 시작 (Ping/Pong)
  private startHeartbeat() {
      this.stopHeartbeat(); // 기존 하트비트 중지
      this.pingInterval = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
              console.log('WebSocket: Sending ping');
              this.ws.send('ping');
              // 핑 전송 후 응답 타임아웃 설정
              this.resetPingTimeout(true);
          }
      }, this.PING_INTERVAL_MS);
  }

  // 하트비트 중지
  private stopHeartbeat() {
      if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
      }
      if (this.pingTimeout) {
          clearTimeout(this.pingTimeout);
          this.pingTimeout = null;
      }
  }

  // 퐁 응답 타임아웃 설정/리셋
  private resetPingTimeout(setNewTimeout = false) {
      if (this.pingTimeout) {
          clearTimeout(this.pingTimeout);
          this.pingTimeout = null;
      }
      if (setNewTimeout) {
          this.pingTimeout = setTimeout(() => {
              console.warn(`WebSocket: No pong received within ${this.PONG_TIMEOUT_MS}ms. Closing connection.`);
              this.ws?.close(1000, "Pong timeout"); // 타임아웃 시 연결 종료
          }, this.PONG_TIMEOUT_MS);
      }
  }


  private handleReconnect() {
    if (!this.isWebSocketEnabled) {
        console.log("Reconnect suppressed: WebSocket is disabled.");
      return;
    }
    if (this.connectionStatus === 'connecting') {
        console.log("Reconnect suppressed: Connection attempt already in progress.");
        return; // 이미 연결 중이면 재시도 안 함
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.lastReconnectAttempt = new Date();
      const delay = Math.pow(1.5, this.reconnectAttempts) * 1000; // 조정된 Exponential backoff
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay / 1000}s...`);
      setTimeout(() => {
        if (this.currentUrl) { // 이전 URL로 재연결 시도
          this.connect(this.currentUrl);
        } else {
            console.warn("Cannot reconnect: No previous URL available.");
        }
      }, delay);
    } else {
      console.warn('Max reconnection attempts reached. Stopping reconnection attempts.');
      // 여기서 isWebSocketEnabled를 false로 설정하여 영구 중단 가능
      // this.isWebSocketEnabled = false;
    }
  }

  private subscribeToAll() {
    if (!this.isWebSocketEnabled || this.connectionStatus !== 'connected') {
      return;
    }
    // 모든 구독자에게 구독 메시지 재전송
    this.subscribers.forEach((callbacks, type) => {
        if (callbacks.size > 0) { // 콜백이 있는 경우에만 구독 메시지 전송
             console.log(`Resubscribing to channel: ${type}`);
             this.sendMessage({ type: 'subscribe', channel: type });
        }
    });
  }

  // 마지막 수신 데이터 복구
  private restoreLastData() {
    if (this.lastReceivedData.size === 0) {
      return;
    }
    console.log(`Restoring ${this.lastReceivedData.size} cached data items after reconnection`);
    this.lastReceivedData.forEach((data, type) => {
      if (this.subscribers.has(type) && this.subscribers.get(type)?.size > 0) {
        console.log(`Restoring cached data for ${type}`);
        this.notifySubscribers(type, data);
      }
    });
  }

  private sendMessage(message: any) {
    if (!this.isWebSocketEnabled || this.connectionStatus !== 'connected') {
        console.warn("Cannot send message: WebSocket not connected or enabled.");
      return false;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
        console.error("Cannot send message: WebSocket readyState is not OPEN.");
        return false;
    }
  }

  // 레거시 테스트/호출부 호환용 공개 메서드
  public send(message: any): boolean {
    return this.sendMessage(message);
  }

  subscribe<T>(type: string, callback: WebSocketCallback<T>) {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }
    const callbacks = this.subscribers.get(type);
    if (callbacks && !callbacks.has(callback)) { // 중복 구독 방지
        callbacks.add(callback);

        // 즉시 연결되어 있다면 구독 메시지 전송
        if (this.isWebSocketEnabled && this.connectionStatus === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
            console.log(`Subscribing to channel: ${type}`);
            this.sendMessage({ type: 'subscribe', channel: type });
        }

        // 캐시된 데이터가 있으면 즉시 전달
        if (this.lastReceivedData.has(type)) {
          try {
            const cachedData = this.lastReceivedData.get(type);
            console.log(`Providing cached data for ${type} to new subscriber`);
            callback(cachedData);
          } catch (error) {
            console.error(`Error providing cached data for ${type}:`, error);
          }
        }
    }
  }

  unsubscribe(type: string, callback: WebSocketCallback<any>) {
    const callbacks = this.subscribers.get(type);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        // 더 이상 해당 타입 구독자가 없으면 구독 취소 메시지 전송 (선택적)
        if (this.isWebSocketEnabled && this.connectionStatus === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
            console.log(`Unsubscribing from channel: ${type}`);
            this.sendMessage({ type: 'unsubscribe', channel: type });
        }
        // this.subscribers.delete(type); // 필요하면 Map에서 키 삭제
      }
    }
  }

  private notifySubscribers(type: string, data: any) {
    const callbacks = this.subscribers.get(type);
    if (callbacks) {
      console.log(`Notifying ${callbacks.size} subscribers for type: ${type}`);
      callbacks.forEach(callback => {
        try {
            callback(data);
        } catch (error) {
            console.error(`Error in WebSocket callback for type ${type}:`, error);
        }
    });
    }
  }

  // 연결 상태 변경 처리
  private setConnectionStatus(status: 'disconnected' | 'connecting' | 'connected') {
    if (this.connectionStatus !== status) {
      const previousStatus = this.connectionStatus;
      this.connectionStatus = status;
      console.log(`WebSocket connection status changed: ${previousStatus} -> ${status}`);
      
      // 연결 상태 변경 구독자에게 통보
      this.connectionStatusSubscribers.forEach(callback => {
        try {
          callback(status);
        } catch (error) {
          console.error('Error in connection status callback:', error);
        }
      });
    }
  }

  // 연결 상태 변경 구독
  public subscribeToConnectionStatus(callback: ConnectionStatusCallback) {
    this.connectionStatusSubscribers.add(callback);
    // 즉시 현재 상태 전달
    try {
      callback(this.connectionStatus);
    } catch (error) {
      console.error('Error in initial connection status callback:', error);
    }
  }

  // 연결 상태 변경 구독 해제
  public unsubscribeFromConnectionStatus(callback: ConnectionStatusCallback) {
    this.connectionStatusSubscribers.delete(callback);
  }

  // 풀의 모든 데이터 요청
  public fetchAllPoolData(poolId: string | number) {
    if (!this.isWebSocketEnabled || this.connectionStatus !== 'connected') {
      console.warn('Cannot fetch pool data: WebSocket not connected or enabled');
      return false;
    }
    
    console.log(`Requesting all data for pool ${poolId}`);
    return this.sendMessage({
      type: 'request',
      data: {
        poolId,
        requestType: 'allData'
      }
    });
  }

  // 특정 타입의 데이터 요청
  public fetchData(type: string, poolId?: string | number) {
    if (!this.isWebSocketEnabled || this.connectionStatus !== 'connected') {
      console.warn(`Cannot fetch ${type} data: WebSocket not connected or enabled`);
      return false;
    }
    
    console.log(`Requesting ${type} data${poolId ? ` for pool ${poolId}` : ''}`);
    return this.sendMessage({
      type: 'request',
      data: {
        type,
        poolId
      }
    });
  }

  // --- 기존 편의 메서드들은 그대로 유지 ---
  subscribeToAlerts(callback: WebSocketCallback<Alert>) { this.subscribe('alerts', callback); }
  subscribeToWaterQuality(callback: WebSocketCallback<WaterQuality>) { this.subscribe('water_quality', callback); }
  subscribeToSafetyStatus(callback: WebSocketCallback<SafetyStatus>) { this.subscribe('safety_status', callback); }
  subscribeToCctvStatus(callback: WebSocketCallback<Cctv>) { this.subscribe('cctv_status', callback); }
  subscribeToWearableStatus(callback: WebSocketCallback<Wearable>) { this.subscribe('wearable_status', callback); }
  unsubscribeFromAlerts(callback: WebSocketCallback<Alert>) { this.unsubscribe('alerts', callback); }
  unsubscribeFromWaterQuality(callback: WebSocketCallback<WaterQuality>) { this.unsubscribe('water_quality', callback); }
  unsubscribeFromSafetyStatus(callback: WebSocketCallback<SafetyStatus>) { this.unsubscribe('safety_status', callback); }
  unsubscribeFromCctvStatus(callback: WebSocketCallback<Cctv>) { this.unsubscribe('cctv_status', callback); }
  unsubscribeFromWearableStatus(callback: WebSocketCallback<Wearable>) { this.unsubscribe('wearable_status', callback); }
  // --- 편의 메서드 끝 ---

   // 연결 상태 반환 메서드 추가
   public getConnectionStatus(): 'disconnected' | 'connecting' | 'connected' {
       return this.connectionStatus;
   }

   public isConnected(): boolean {
      return this.connectionStatus === 'connected';
   }

   // WebSocket 활성화 상태 설정 (예: 명시적 disconnect 후 다시 활성화)
   public enableWebSocket() {
       this.isWebSocketEnabled = true;
       console.log("WebSocket auto-reconnect enabled.");
   }

   /**
    * 재연결 시도 횟수를 반환
    */
   public getReconnectAttempts(): number {
     return this.reconnectAttempts;
   }
   
   /**
    * 마지막 연결 시도 시간을 반환
    */
   public getLastAttemptTime(): Date | null {
     return this.lastReconnectAttempt;
   }
   
   /**
    * 연결 시작 시간을 반환
    */
   public getConnectedSince(): Date | null {
     return this.connectedSince;
   }
   
   /**
    * 연결 유지 시간(초)을 반환
    */
   public getUptimeSeconds(): number {
     if (!this.connectedSince) return 0;
     return Math.floor((new Date().getTime() - this.connectedSince.getTime()) / 1000);
   }
}

// 싱글톤 인스턴스 내보내기
export const monitoringWebSocket = new WebSocketService(); 
export const sensorWebSocket = new WebSocketService('sensors');